import { useEffect, useState, useCallback } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { debounce } from 'lodash';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  abbreviatedRegionToCurrencySymbol,
  countryToCurrencySymbol,
} from '@root/shared/currencyByCountry';
import useEnv from '@shared/useEnv';
import actions from '@store/actions/listingSearchPage';
import Input from '@components/shared/Form/Input/Input';
import { ListingSearchFilterTypes, PriceRangeFilter, StoreState, CurrentUser } from '@root/types';
import Checkbox from '@components/shared/forms/RawCheckbox';
import useDebounce from '@shared/useDebounce';

import s from './PriceFilter.module.less';
import useHighlightedClick from '../useHighlightedClick';

type Props = {
  criteria: ListingSearchCriteria;
  onChange: (criteria: () => ListingSearchCriteria) => void;
  type: ListingSearchFilterTypes;
};

type minOrMaxField = 'minPrice' | 'maxPrice';

const buildAnalyticStringValue = (priceFilter: PriceRangeFilter): string =>
  `${priceFilter.minPrice.length > 0 ? priceFilter.minPrice : 0}-${priceFilter.maxPrice}`;

const debounceLength = 1000;

const PriceFilter = ({ onChange, criteria, type }: Props) => {
  const flags = useFlags();
  const { t } = useTranslation('filters');
  const { locale } = useEnv();
  const [priceFilter, setPriceFilter] = useState(criteria.priceFilter());
  const currentUser = useSelector((state: StoreState) => state.currentUser);
  const marketsBySlug = useSelector((state: StoreState) => state.shapes.bySlug);
  const debouncedCriteria = useDebounce(criteria, debounceLength);
  const { setHighlighted, isHighlighted } = useHighlightedClick();
  const dispatch = useDispatch();
  const slug = criteria.neighborhoods()[0];
  const selectedMarket = marketsBySlug[slug];

  const currencySymbol = (market: any | null, user: CurrentUser | null): string => {
    const userLocale = user?.languagePreference || locale || 'en-us';

    if (market) return abbreviatedRegionToCurrencySymbol(market.abbreviatedRegion, userLocale);
    if (currentUser) return countryToCurrencySymbol(currentUser.marketCountryCode, userLocale);
    return countryToCurrencySymbol('US', userLocale);
  };

  const onPriceNegotiableToggle = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const priceNegotiable = e.target.checked;
    setHighlighted('priceNegotiable');
    setPriceFilter(oldPriceFilter => {
      const newPriceFilter = { ...oldPriceFilter, priceNegotiable };
      criteria.changePriceFilter(newPriceFilter);
      onChange(() => {
        dispatch(
          actions.priceFilterChanged(
            buildAnalyticStringValue(newPriceFilter),
            newPriceFilter.priceNegotiable,
          ),
        );

        return criteria;
      });
      return newPriceFilter;
    });
  };

  const onPriceFilterInputChanged = (priceValue: string, minOrMax: minOrMaxField): void => {
    setPriceFilter(oldPriceFilter => {
      const newPriceFilter = {
        ...oldPriceFilter,
        [minOrMax]: priceValue,
        priceNegotiable: oldPriceFilter.priceNegotiable,
      };

      updateCriteria.cancel();
      updateCriteria(newPriceFilter);

      return newPriceFilter;
    });
  };

  const updateCriteria = useCallback(
    debounce(newPriceFilter => {
      onChange(() => {
        criteria.changePriceFilter(newPriceFilter);
        dispatch(
          actions.priceFilterChanged(
            buildAnalyticStringValue(newPriceFilter),
            newPriceFilter.priceNegotiable,
          ),
        );
        return criteria;
      });
    }, debounceLength),
    [criteria],
  );

  useEffect(() => {
    const prices = criteria.priceFilter();

    // the only time we want to react to this is when clearing
    if (prices.minPrice === '' && prices.maxPrice === '') {
      setPriceFilter(prices);
    }
  }, [debouncedCriteria]);

  return (
    <>
      <div className={s.rangeInputContainer}>
        <span className={s.currency} data-testid="min-currency">
          {flags['public-london-market']
            ? currencySymbol(selectedMarket, currentUser)
            : t('price.currencySymbol')}
        </span>
        <Input
          labelText={t('price.minPrice')}
          inputClassName={s.input}
          name={`minPrice-${type}`}
          type="number"
          value={priceFilter.minPrice}
          onChange={(priceValue: string) => onPriceFilterInputChanged(priceValue, 'minPrice')}
        />
        <span className={s.rangeUnit}>{t('price.sf/yr')}</span>
      </div>
      <div className={s.rangeInputContainer}>
        <span className={s.currency} data-testid="max-currency">
          {flags['public-london-market']
            ? currencySymbol(selectedMarket, currentUser)
            : t('price.currencySymbol')}
        </span>
        <Input
          labelText={t('price.maxPrice')}
          inputClassName={s.input}
          name={`maxPrice-${type}`}
          type="number"
          value={priceFilter.maxPrice}
          onChange={(priceValue: string) => onPriceFilterInputChanged(priceValue, 'maxPrice')}
        />
        <span className={s.rangeUnit}>{t('price.sf/yr')}</span>
      </div>
      <ul className={s.priceNegotiable}>
        <li className={`${isHighlighted('priceNegotiable') ? s.highlighted : ''}`}>
          <Checkbox
            // className={s.checkbox}
            checked={priceFilter.priceNegotiable}
            name={`priceNegotiable-${type}`}
            onChange={onPriceNegotiableToggle}
          >
            {t('price.priceNegotiable')}
          </Checkbox>
        </li>
      </ul>
    </>
  );
};

export default PriceFilter;
